import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Typography } from '@mui/material';

import signInFormValidation from 'pages/sign-in/validation/sign-in-form-validation';
import { selectEnterprise } from 'base/core/selectors';
import logo from 'assets/images/slocoach-logo.svg';

import { GuestLayout } from 'components/templates';
import { selectLoader } from 'base/auth/selectors';
import { authSliceActions } from 'base/auth/slice';
import useAuthentication from 'base/auth/hooks/use-authentication';
import SignInFormView from '../sign-in-form';

const SignInView = () => {
  const navigate = useNavigate();
  const methods = useForm({
    defaultValues: {
      name: '',
      password: '',
      retainLogin: false,
    },
    resolver: yupResolver(signInFormValidation),
    shouldFocusError: true,
  });
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const enterprice = useSelector(selectEnterprise);
  const location = useLocation();
  const callbackPath = new URLSearchParams(location.search).get('callbackPath');

  const { isAuthenticated } = useAuthentication();

  const handleSubmitFrom: SubmitHandler<any> = useCallback(
    (data) => {
      dispatch(authSliceActions.signIn(data));
    },
    [dispatch],
  );

  useEffect(() => {
    const authenticated = isAuthenticated();
    if (authenticated) {
      window.location.href = callbackPath ?? '/';
    } if (!authenticated && loading) {
      dispatch(authSliceActions.signOut());
    }
  }, []);

  useEffect(() => {
    dispatch(authSliceActions.setCallbackPath(callbackPath || '/'));
  }, [callbackPath, dispatch]);

  const formWrapperStyles = { width: { xs: '100%', md: '600px' } };

  return (
    <GuestLayout loading={loading}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        className="text-center py-4"
      >
        <Grid item sx={formWrapperStyles}>
          <Typography
            variant="h2"
            sx={{
              mb: 6,
              fontSize: {
                xs: '20px', sm: '22px', md: '24px', lg: '40px',
              },
            }}
          >
            {enterprice.name
              ? `Welcome to ${enterprice.name}` : 'Welcome to Slocoach'}
          </Typography>
          <FormProvider {...methods}>
            <SignInFormView onSubmit={handleSubmitFrom} />
          </FormProvider>
          <div className="text-center mt-4 pt-2">
            <img src={logo} alt="Slo-Coach" />
          </div>
        </Grid>
      </Grid>
    </GuestLayout>
  );
};

export default SignInView;
